import { Box, Typography } from "@mui/material";
import { useCollections } from "api/collections";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import { sortCampaignTranslations } from "components/Layout/Campaign/Common/campaignHelpers";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import {
  backendErrors,
  parseTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import {
  BECollectionFormat,
  FECollectionFormat,
  SingleCollectionData,
} from "../types";
import { BasicSettings } from "./BasicSettings";
import { useStyles } from "./CollectionForm.styles";
import { CollectionStatus } from "./CollectionStatus";

interface Props {
  collectionData?: SingleCollectionData;
  editMode?: boolean;
}

export const CollectionForm = ({ collectionData, editMode }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const dataTestId = editMode ? "edit-group" : "add-group";
  const { id: collectionId } = useParams<{ id: string }>();
  const { addCollection, editCollection } = useCollections();

  const initialValues: FECollectionFormat =
    editMode && collectionData
      ? {
          translations:
            collectionData.translations &&
            sortCampaignTranslations(
              defaultLang?.code,
              Object.keys(collectionData.translations).map((key) => ({
                name: collectionData?.translations[key]?.name,
                description: collectionData?.translations[key]?.description,
                code: key,
              })),
            ),
          active: collectionData.active,
        }
      : {
          translations: [
            defaultLang && {
              name: "",
              description: "",
              code: defaultLang.code,
            },
          ],
          active: false,
        };

  const transformTranslations = (
    translations: FECollectionFormat["translations"],
  ): {} => {
    return translations.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.code as string]: {
          name: cur.name,
          description: cur.description,
        },
      }),
      {},
    );
  };

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});

        const properties: BECollectionFormat = {
          ...values,
          translations: transformTranslations(values.translations),
        };

        try {
          editMode
            ? await editCollection(collectionId as string, properties)
            : await addCollection(properties);
          notify(
            message({
              id: editMode ? "collectionUpdated" : "collectionAdded",
            }),
          );
          navigate(links.collectionsList);
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          const parsedErrors = {
            ...errors,
            translations: parseTranslationsArrayError(
              errors?.translations,
              values?.translations,
            ),
          };
          setStatus(parsedErrors);
          focusError();
        }
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        values,
        dirty,
        initialValues,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            {status?.[""] && (
              <span className={`${classes.error} error-anchor`}>
                {status[""]}
              </span>
            )}
            <Typography className={classes.sectionHeader}>
              {message({ id: "basicSettings" })}
            </Typography>
            <BasicSettings
              editMode={editMode}
              data-test-id={`${dataTestId}-basic-settings`}
            />
            <Typography className={classes.sectionHeader}>
              {message({ id: "collectionStatus" })}
            </Typography>
            <CollectionStatus data-test-id={`${dataTestId}-group-status`} />
            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => navigate(links.collectionsList)}
              onSubmitClick={() => handleSubmit()}
              {...(!editMode && { submitMessage: "addCollection" })}
            />

            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
